import {
  Box,
  Text,
  Card,
  Grid,
  Badge,
  Group,
  Image,
  Divider,
  Loader,
  Skeleton,
} from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import databricksIcon from '../../assets/databricks_icon.png'
import dbtIcon from '../../assets/dbt_icon.png'
import prismLogo from '../../assets/prism_logo_tagline.svg'
import { withLoginHint } from './loginHint'
import {
  checkWorkspaceAccess,
  type WorkspaceAccess,
} from '../../api/workspaceAccess'
import { isAllowlisted } from '../../api/allowlist'

const DATABRICKS_DEV_HOST = import.meta.env.VITE_DATABRICKS_DEV_HOST as
  string | undefined
const DATABRICKS_PROD_HOST = import.meta.env.VITE_DATABRICKS_PROD_HOST as
  string | undefined
const DBT_CLOUD_HOST = import.meta.env.VITE_DBT_CLOUD_HOST as string | undefined

// Card styling shared by every workspace tile.
const cardStyle = (borderColor: string) =>
  ({
    borderColor,
    borderWidth: '2px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    transition: 'all 0.2s ease',
  }) as const

export function Dashboard() {
  const allowed = isAllowlisted()
  const { data: access, isLoading } = useQuery<WorkspaceAccess>({
    queryKey: ['workspace-access'],
    queryFn: checkWorkspaceAccess,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    enabled: allowed,
  })

  const handleWorkspaceClick = (environment: 'dev' | 'prod') => {
    const workspaceUrls = {
      dev:
        DATABRICKS_DEV_HOST ??
        'https://adb-7405610704450128.8.azuredatabricks.net',
      prod:
        DATABRICKS_PROD_HOST ??
        'https://adb-7405609970704482.2.azuredatabricks.net',
    }

    // Read the signed-in MSAL user synchronously so window.open stays inside
    // the click gesture (avoids pop-up blocking).
    const loginHint = localStorage.getItem('prism_username')
    window.open(
      withLoginHint(workspaceUrls[environment], loginHint),
      '_blank',
      'noopener,noreferrer'
    )
  }

  const handleDbtCloudClick = () => {
    const loginHint = localStorage.getItem('prism_username')
    window.open(
      withLoginHint(DBT_CLOUD_HOST ?? 'https://xd165.us2.dbt.com/home', loginHint),
      '_blank',
      'noopener,noreferrer'
    )
  }

  // While probing, show skeleton placeholders so the layout doesn't jump.
  // Non-allowlisted users don't have Databricks access -- skip the tiles.
  const showDev = allowed && (isLoading || access?.dev !== false)
  const showProd = allowed && (isLoading || access?.prod !== false)
  // dbt Cloud tile is hidden for non-allowlisted users.
  const showDbt = allowed && Boolean(DBT_CLOUD_HOST)
  const showAnyCard = showDev || showProd || showDbt

  return (
    <Box>
      {/* Page Header */}
      <Box ta="center" mb="sm">
        <Box
          mx="auto"
          mb="xs"
          px="xl"
          py="xs"
          maw={720}
          style={{
            backgroundColor: '#1a1a1a',
            borderRadius: '12px',
          }}
        >
          <Image
            src={prismLogo}
            alt="Prism — Platform for Reporting, Intelligence, Science, and Machine Learning"
            w={660}
            h="auto"
            mx="auto"
          />
        </Box>
        <Text
          size="md"
          fs="italic"
          fw={600}
          maw={600}
          mx="auto"
          mb="md"
          style={{ color: '#3B0764' }}
        >
          Democratizing data, high-performance analytics, and unified governance
          at scale, engineered natively on our secure Microsoft Azure
          infrastructure.
        </Text>
      </Box>

      {showAnyCard && <Divider maw={600} mx="auto" mb="xl" />}

      {/* Workspace Portal */}
      <Grid maw={600} mx="auto" mb="md" justify="center">
        {showDev && (
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Skeleton visible={isLoading} h={180} w={180} mx="auto" radius="md">
              <Card
                shadow="lg"
                p="md"
                h={180}
                w={180}
                mx="auto"
                withBorder
                style={cardStyle('var(--mantine-color-prism-3)')}
                onClick={() => handleWorkspaceClick('dev')}
                className="workspace-card"
              >
                <Box
                  bg="prism.0"
                  style={{
                    borderRadius: '50%',
                    width: '56px',
                    height: '56px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '12px',
                  }}
                >
                  <Image src={databricksIcon} alt="Databricks" w={30} h={30} />
                </Box>
                <Text size="sm" fw={700} c="gray.9" mb="xs">
                  Development
                </Text>
                <Group gap="xs">
                  <Badge size="xs" color="prism" variant="dot">
                    Active
                  </Badge>
                </Group>
              </Card>
            </Skeleton>
          </Grid.Col>
        )}

        {showProd && (
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Skeleton visible={isLoading} h={180} w={180} mx="auto" radius="md">
              <Card
                shadow="lg"
                p="md"
                h={180}
                w={180}
                mx="auto"
                withBorder
                style={cardStyle('var(--mantine-color-prism-4)')}
                onClick={() => handleWorkspaceClick('prod')}
                className="workspace-card"
              >
                <Box
                  bg="prism.1"
                  style={{
                    borderRadius: '50%',
                    width: '56px',
                    height: '56px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '12px',
                  }}
                >
                  <Image src={databricksIcon} alt="Databricks" w={30} h={30} />
                </Box>
                <Text size="sm" fw={700} c="gray.9" mb="xs">
                  Production
                </Text>
                <Group gap="xs">
                  <Badge size="xs" color="prism" variant="dot">
                    Active
                  </Badge>
                </Group>
              </Card>
            </Skeleton>
          </Grid.Col>
        )}

        {showDbt && (
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Card
              shadow="lg"
              p="md"
              h={180}
              w={180}
              mx="auto"
              withBorder
              style={cardStyle('var(--mantine-color-prism-5)')}
              onClick={handleDbtCloudClick}
              className="workspace-card"
            >
              <Box
                bg="prism.2"
                style={{
                  borderRadius: '50%',
                  width: '56px',
                  height: '56px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '12px',
                }}
              >
                <Image src={dbtIcon} alt="dbt" w={30} h={30} />
              </Box>
              <Text size="sm" fw={700} c="gray.9" mb="xs">
                dbt Cloud
              </Text>
              <Group gap="xs">
                <Badge size="xs" color="prism" variant="dot">
                  Available
                </Badge>
              </Group>
            </Card>
          </Grid.Col>
        )}
      </Grid>

      {isLoading && (
        <Group justify="center" mb="md">
          <Loader size="xs" />
          <Text size="sm" c="dimmed">
            Checking workspace access…
          </Text>
        </Group>
      )}

      {showAnyCard && (
        <Text size="lg" c="gray.6" maw={600} mx="auto" ta="center">
          Access your Databricks workspaces and dbt Cloud environments for
          development, production, and transformation workflows
        </Text>
      )}
    </Box>
  )
}
